# Remote Logging — card 6aac4153fb9b132bb83a8911.
#
# WHAT THIS REPLACES. The About dialog's "Remote Logging" switch wrote a
# localStorage key named after a third-party vendor. The vendor shut down on
# 2026-09-16, and nothing in this repo has read that key since — grep it: the
# only readers are the three toggles themselves. So the switch said "On",
# nothing was captured, and an agent who talked a customer through turning it
# on was misleading them without knowing it.
#
# WHAT IT IS NOW. Arming asks the framework for a Session row of our own
# (RemoteConsoles/armRemoteConsole) and loads the framework's remote-console bundle
# against it; the browser's console output and uncaught errors go to that row,
# and support reads the tail of it through RemoteConsoles/recentLogsFor. Off means
# off: no bundle, no row, no traffic.
#
# THE SESSION ID IS SHOWN TO THE USER ON PURPOSE. Support has to be able to
# find the row, and the customer reading six characters down the phone is the
# shortest path to that. It is not a credential — writing to that session
# requires being the principal who armed it.
#
# ONE MODULE FOR THREE SHELLS. /app, /teacher and /org each have their own
# title bar and each had its own copy of the dead toggle. The state lives under
# one storage key here so that arming in one shell is visible in the others,
# which is what a customer who is told "turn on remote logging" expects.

# `expiresAt` is stored alongside the id because the SERVER decides the armed
# window (RemoteConsole.coffee REMOTE_CONSOLE_MAX_S) and the client must not outlive
# it — an expired arm that keeps loading the bundle is a browser posting into a
# session that will only ever answer 410.
STORAGE_KEY = 'remoteConsoleSession'

read = ->
  try
    raw = localStorage.getItem STORAGE_KEY
    return null unless raw
    rec = JSON.parse raw
    return null unless rec?.sessionId
    # `>=`, not `>`. At the boundary — a server that answers
    # expiresInSeconds 0, or a clock that has not moved between the arm and the
    # first read — a strict `>` calls the record live for that millisecond, and
    # the bundle loads against a session the server will only ever answer 410
    # for. Expired is expired.
    return null if rec.expiresAt? and Date.now() >= rec.expiresAt
    rec
  catch err
    # Storage refused (private window, blocked site data): the feature is off
    # rather than broken.
    null

writeRecord = (rec) ->
  try localStorage.setItem STORAGE_KEY, JSON.stringify(rec) catch err then null

clearRecord = ->
  try localStorage.removeItem STORAGE_KEY catch err then null

# The framework's bundle reads its whole configuration off this global, so it
# is set BEFORE the script tag is appended. The token is the session we already
# hold — the bundle grants nothing its holder does not already have, because
# the server refuses any write to a session this principal did not arm.
# WHERE THE API ACTUALLY IS, and it is not window.clientAPI. Each el-feet shell
# constructs its ClientAPI as a module local and hands it to Application, which
# keeps it as `loopbackAPI` — so `window.clientAPI` is undefined in /app,
# /teacher and /org alike. Reading only that name meant this module could never
# arm in the real app; caught by driving the shipped bundle in a browser on
# ripper3ws1, 2026-09-17, not by any unit case, because a fake browser is
# whatever the fake says it is. window.clientAPI is kept first for the shells
# that do publish it (the framework's own admin shell does).
clientApi = -> window.clientAPI ? window.app?.loopbackAPI

loadBundle = (rec) ->
  return if window.__REMOTE_CONSOLE_STARTED__
  api = clientApi()
  return unless api?
  base = (api.baseURL or '').replace(/\/$/, '')
  window.__REMOTE_CONSOLE__ =
    sessionId: rec.sessionId
    baseURL: base
    token: api.headers?.Authorization
    expiresInSeconds: Math.max(Math.floor(((rec.expiresAt or 0) - Date.now()) / 1000), 0)
  el = document.createElement 'script'
  el.src = "#{base}/remote-console.js"
  el.async = true
  document.head.appendChild el

export sessionId = -> read()?.sessionId or null
export isArmed = -> read()?

# Arm: the server creates the Session and tells us how long it will accept
# output for. Returns the session id so the caller can show it.
export arm = ->
  api = clientApi()
  throw new Error('not signed in') unless api?.RemoteConsole?.armRemoteConsole?
  # NO ARGUMENT. armRemoteConsole's first declared parameter is `parentKey`, a
  # string, and the framework maps a first positional argument onto it — so
  # `armRemoteConsole {}` sends an OBJECT where a string is declared and the
  # server answers "parentKey is an invalid type for armRemoteConsole. expected
  # string, got object." The empty object reads like "no options" and is not:
  # both parameters are optional, so passing nothing is how you say nothing.
  # Measured against the real app on ripper3ws1, 2026-09-17.
  res = await api.RemoteConsole.armRemoteConsole()
  # Axios-shaped: the row is in res.data (helpers/CLIENT_API_CONVENTIONS.md).
  body = res?.data ? res
  throw new Error('the server armed no session') unless body?.sessionId
  rec =
    sessionId: body.sessionId
    expiresAt: Date.now() + ((body.expiresInSeconds or 0) * 1000)
  writeRecord rec
  loadBundle rec
  rec.sessionId

# Disarm: tell the server first, so a browser that is closed before it can stop
# the bundle is still not accepted. Clearing locally happens whatever the
# server said — a user who turns it off has turned it off.
export disarm = ->
  rec = read()
  clearRecord()
  try window.__REMOTE_CONSOLE_STOP__?() catch err then null
  return unless rec?.sessionId
  try
    await clientApi()?.RemoteConsole?.disarmRemoteConsole? {id: rec.sessionId}
  catch err
    console.warn "[remote logging] could not disarm #{rec.sessionId} on the server: #{err?.message or err}"

# Called once at boot: a session armed before a reload keeps logging across it,
# which is the whole point — the customer arms it, reproduces the problem, and
# the reproduction usually involves navigating.
export restore = ->
  rec = read()
  return unless rec?
  loadBundle rec
